/**
 * Conditional Logic Utilities
 *
 * This module contains functions to calculate conditional values based on
 * workshop, company, and type selections for parts and labor calculations.
 */

import {
  PartsVariables,
  LaborVariables,
  DISCOUNT_MATRIX,
  LABOR_COST_MATRIX,
  BRAND_OPTIONS,
} from '../types';

/**
 * Calculate discount percentage based on parts variables
 * @param partsVariables - The parts variables (workshop, company, brand)
 * @returns The calculated discount percentage
 */
export function calculateConditionalDiscount(
  partsVariables: PartsVariables,
): number {
  const { workshop, company } = partsVariables;
  return DISCOUNT_MATRIX[workshop][company];
}

/**
 * Calculate labor cost based on labor variables
 * @param laborVariables - The labor variables (workshop, type)
 * @returns The calculated labor cost
 */
export function calculateConditionalLaborCost(
  laborVariables: LaborVariables,
): number {
  const { workshop, type } = laborVariables;
  return LABOR_COST_MATRIX[workshop][
    type as keyof typeof LABOR_COST_MATRIX[typeof workshop]
  ];
}

/**
 * Get available brands for a given company
 * @param company - The company type
 * @returns Array of available brands
 */
export function getAvailableBrands(
  company: 'Purdy Motor' | 'Automotriz',
): string[] {
  return [...BRAND_OPTIONS[company]];
}

/**
 * Validate parts variables combination
 * @param partsVariables - The parts variables to validate
 * @returns Object with validation result and error message
 */
export function validatePartsVariables(
  partsVariables: PartsVariables,
): {
  isValid: boolean;
  error?: string;
} {
  const { company, brand } = partsVariables;

  // Check if brand is valid for the selected company
  const availableBrands = getAvailableBrands(company);
  if (!availableBrands.includes(brand)) {
    return {
      isValid: false,
      error: `La marca "${brand}" no está disponible para la empresa "${company}"`,
    };
  }

  return { isValid: true };
}

/**
 * Validate labor variables combination
 * @param laborVariables - The labor variables to validate
 * @returns Object with validation result and error message
 */
export function validateLaborVariables(
  laborVariables: LaborVariables,
): {
  isValid: boolean;
  error?: string;
} {
  const { workshop, type } = laborVariables;

  // Check if the combination is valid
  if (workshop === 'Purdy Auto' && type !== 'Interno') {
    return {
      isValid: false,
      error: 'Purdy Auto solo puede ser de tipo "Interno"',
    };
  }

  if (workshop === 'Otros Talleres' && type !== 'Externo') {
    return {
      isValid: false,
      error: 'Otros Talleres solo puede ser de tipo "Externo"',
    };
  }

  return { isValid: true };
}

/**
 * Get default parts variables
 * @returns Default parts variables
 */
export function getDefaultPartsVariables(): PartsVariables {
  return {
    workshop: 'Purdy Auto',
    company: 'Purdy Motor',
    brand: 'Toyota',
  };
}

/**
 * Get default labor variables
 * @returns Default labor variables
 */
export function getDefaultLaborVariables(): LaborVariables {
  return {
    workshop: 'Purdy Auto',
    type: 'Interno',
  };
}

/**
 * Calculate conditional values for both parts and labor
 * @param partsVariables - Parts variables
 * @param laborVariables - Labor variables
 * @param isJsonLoaded - Whether values come from JSON (invalidates labor cost calculation)
 * @returns Object with calculated conditional values
 */
export function calculateAllConditionalValues(
  partsVariables: PartsVariables,
  laborVariables: LaborVariables,
): {
  laborCost: number;
  partsDiscount: number;
  bodyworkRateCost: number;
  paintRateCost: number;
  mechanicalRateCost: number;
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Validate parts variables
  const partsValidation = validatePartsVariables(partsVariables);
  if (!partsValidation.isValid) {
    errors.push(partsValidation.error!);
  }

  // Validate labor variables
  const laborValidation = validateLaborVariables(laborVariables);
  if (!laborValidation.isValid) {
    errors.push(laborValidation.error!);
  }

  // Calculate values
  const partsDiscount = calculateConditionalDiscount(partsVariables);

  // If loaded from JSON, don't calculate labor cost from matrix
  // This prevents overriding JSON values with matrix values
  const laborCost = calculateConditionalLaborCost(laborVariables);

  return {
    laborCost,
    partsDiscount,
    bodyworkRateCost: laborCost,
    paintRateCost: laborCost,
    mechanicalRateCost: laborCost,
    isValid: errors.length === 0,
    errors,
  };
}
